import React, { useMemo } from "react";
import { PlusIcon } from "@/components/icons";
import { PanelHeaderAction } from "@/components/ui/PanelHeaderAction";
import { ENTITY_CONFIG } from "@/core/entityConfig";
import { useTrailNavOptions } from "@/core/navigation/trail";
import { WorkspacePanel } from "@/domains/Ideas/components/WorkspacePanel";
import { TaskListEditor } from "@/domains/Tasks/components/TaskListEditor";
import { TaskDrawer } from "@/domains/Tasks/drawer/TaskDrawer";
import { clientContacts } from "@/domains/Tasks/drawer/taskDrawerForm";
import { useTaskDrawer } from "@/domains/Tasks/drawer/useTaskDrawer";
import { useClientPeopleQuery, useStandaloneTasksQuery, useToggleTask } from "@/domains/Tasks/hooks/useTasks";
import { capabilitiesFor } from "@/domains/Tasks/taskCapabilities";
import type { Task, TaskAssigneeOption, TaskList } from "@/domains/Tasks/types";

interface TasksPanelProps {
  clientId: string;
  collapsed: boolean;
  onToggleCollapse: () => void;
}

const NO_TASKS: Task[] = [];
const NO_PEOPLE: TaskAssigneeOption[] = [];
const ADMIN = capabilitiesFor("admin");

/** The client's tasks linked to no item (DD-019). One task drawer. New Task
 * creates a task with no links and the client's contacts tagged, each
 * removable (DD-053). */
export const TasksPanel: React.FC<TasksPanelProps> = ({
  clientId,
  collapsed,
  onToggleCollapse,
}) => {
  const { data: tasks = NO_TASKS, isLoading } = useStandaloneTasksQuery(clientId);
  const { data: people = NO_PEOPLE, isLoading: peopleLoading } = useClientPeopleQuery(clientId);
  const toggleTask = useToggleTask();
  const drawer = useTaskDrawer();
  const trailNavOptions = useTrailNavOptions(ENTITY_CONFIG.home.label);
  const list = useMemo<TaskList>(() => ({ tasks, via: [] }), [tasks]);
  const TaskEntityIcon = ENTITY_CONFIG.task.icon;
  const doneCount = tasks.filter((t) => t.status === "done").length;

  return (
    <>
      <WorkspacePanel
        id="tasks"
        title={ENTITY_CONFIG.task.plural}
        icon={<TaskEntityIcon className="w-4 h-4" />}
        summary={tasks.length > 0 ? `${doneCount}/${tasks.length} done` : undefined}
        actions={
          <PanelHeaderAction
            label={ENTITY_CONFIG.task.createLabel}
            icon={<PlusIcon className="w-4 h-4" />}
            // The client's contacts start tagged, so the task shows in their portal (DD-053).
            onClick={() => drawer.create({ clientId, links: [], people: clientContacts(people) })}
            disabled={peopleLoading}
          />
        }
        loading={isLoading}
        collapsed={collapsed}
        onToggleCollapse={onToggleCollapse}
        className={collapsed ? "" : "h-full"}
      >
        <TaskListEditor
          list={list}
          capabilities={ADMIN}
          onToggle={toggleTask}
          onOpen={drawer.open}
          emptyHint="Client-level requests and reminders — not tied to an idea"
        />
      </WorkspacePanel>
      <TaskDrawer drawer={drawer} capabilities={ADMIN} navOptions={trailNavOptions} />
    </>
  );
};
